import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Skeleton, Snackbar, Alert,
  CircularProgress, Chip, Tooltip, Tabs, Tab
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import FlowSettingsPanel from './FlowSettingsPanel';
import ConversationScript from './ConversationScript';
import ConstraintTab from './ConstraintTab';
import voiceFlowService from '../../services/voiceFlowService';

// CloudFront 音檔 base URL
const AUDIO_CDN_BASE = 'https://d3uyjtuwlqw6e9.cloudfront.net/static/voice-assets/duckduck_v1';

// 與 asterisk.service.js _resolveAudioPath 一致的路徑解析
const TOTAL_PREFIXES = [
  'greeting_', 'ask_', 'checking_', 'availability_', 'booking_',
  'seats_', 'confirm_', 'modify_', 'processing_', 'clarify_',
  'festival_', 'group_', 'goodbye', 'final_', 'real_man_',
  'question_', 'any_services_', 'continue_', 'music_on_hold',
  'callback_', 'case_5_', 'case_6_', 'ack_', 'constraint_',
  'scanning_', 'no_avail_', 'has_slot_', 'all_day_', 'second_date_', 'faq_',
];

const CASE_PATTERNS = [
  { re: /^case_1_ordering/, dir: 'case_1_ordering_gsm' },
  { re: /^case_2_check_order/, dir: 'case_2_check_order_gsm' },
  { re: /^case_3_cancel_order/, dir: 'case_3_cancel_order_gsm' },
  { re: /^case_4_modify_order/, dir: 'case_4_modify_order_gsm' },
  { re: /^case_7_group_booking/, dir: 'case_7_group_booking_gsm' },
];

// 解析單一 audio_asset → CloudFront URL
const resolveAudioUrl = (audioAsset) => {
  const fileBase = audioAsset.replace(/\.(gsm|wav|mp3)$/i, '');
  if (fileBase.includes('/')) return `${AUDIO_CDN_BASE}/${fileBase}.wav`;
  if (TOTAL_PREFIXES.some(p => fileBase.startsWith(p))) {
    return `${AUDIO_CDN_BASE}/total_response/${fileBase}.wav`;
  }
  for (const { re, dir } of CASE_PATTERNS) {
    if (re.test(fileBase)) return `${AUDIO_CDN_BASE}/${dir}/${fileBase}.wav`;
  }
  return `${AUDIO_CDN_BASE}/${fileBase}.wav`;
};

// 解析 audio_segments 中的 asset 類型 → CloudFront URL 陣列
// category 對應 S3 子目錄結構
const CATEGORY_DIR_MAP = {
  general: 'general/general_phrases',
  time: 'time/case_time',
  date: 'date/case_date',
  surname: 'case_surname',
  surname_greeting: 'case_surname_greeting',
  party_count: 'case_party_count',
  phone_digits: 'case_phone_numbers',
  duck: 'duck_voices',
};

const resolveSegmentUrl = (segment) => {
  if (segment.type !== 'asset') return null;
  const key = segment.key;
  if (!key) return null;
  // 先用 category 映射
  const category = segment.category;
  const subcategory = segment.subcategory;
  if (category && CATEGORY_DIR_MAP[category]) {
    const dir = subcategory
      ? `${CATEGORY_DIR_MAP[category]}/${subcategory}`
      : CATEGORY_DIR_MAP[category];
    return `${AUDIO_CDN_BASE}/${dir}/${key}.wav`;
  }
  // 沒有 category → 用 audio_asset 的前綴解析
  return resolveAudioUrl(key);
};

// 依序播放多個音檔
const playAudioSequence = (urls, currentAudioRef, onPlayStateChange) => {
  if (!urls.length) { onPlayStateChange(false); return; }
  let idx = 0;
  const playNext = () => {
    if (idx >= urls.length) {
      currentAudioRef.current = null;
      onPlayStateChange(false);
      return;
    }
    const audio = new Audio(urls[idx]);
    currentAudioRef.current = audio;
    audio.onended = () => { idx++; playNext(); };
    audio.onerror = () => { idx++; playNext(); }; // 跳過失敗的片段
    audio.play().catch(() => { idx++; playNext(); });
  };
  onPlayStateChange(true);
  playNext();
};

const ScriptEditor = ({ flowId, flowMeta, onBack }) => {
  // 腳本資料
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 編輯追蹤
  const [editedVariables, setEditedVariables] = useState({});
  const [editedFaqInterrupt, setEditedFaqInterrupt] = useState(null);
  const [textEdits, setTextEdits] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // UI 狀態
  const [activePathId, setActivePathId] = useState('');
  const [activeTab, setActiveTab] = useState('script'); // 'script' | 'constraints'
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const currentAudioRef = React.useRef(null);

  // TTS 語音產生狀態
  const [regeneratingNodes, setRegeneratingNodes] = useState(new Set());
  const [regeneratedUrls, setRegeneratedUrls] = useState({});

  // 載入腳本
  const loadScript = useCallback(async () => {
    setLoading(true);
    const result = await voiceFlowService.getScript(flowId);
    if (result.success) {
      setScript(result.data);
      setEditedVariables({});
      setEditedFaqInterrupt(null);
      setTextEdits([]);
      setHasChanges(false);
      // 預設選第一個路徑
      if (result.data.conversationPaths?.length > 0) {
        setActivePathId(result.data.conversationPaths[0].id);
      }
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setLoading(false);
  }, [flowId]);

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  // 變數編輯
  const handleVariableChange = (key, value) => {
    setEditedVariables(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // FAQ 中斷設定編輯
  const handleFaqInterruptChange = (field, value) => {
    setEditedFaqInterrupt(prev => {
      const current = prev || { ...(script?.faqInterrupt || {}) };
      return { ...current, [field]: value };
    });
    setHasChanges(true);
  };

  // 文字編輯（來自 ScriptBubble）
  const handleTextChange = (nodeId, field, value) => {
    setTextEdits(prev => {
      // 移除同 nodeId+field 的舊編輯
      const filtered = prev.filter(e => !(e.nodeId === nodeId && e.field === field));
      return [...filtered, { nodeId, field, value }];
    });

    // 即時更新 script 中的文字（樂觀更新）
    setScript(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      updated.conversationPaths = prev.conversationPaths.map(path => ({
        ...path,
        steps: path.steps.map(step => {
          if (step.nodeId !== nodeId) return step;
          const newStep = { ...step };
          if (field === 'text' || field === 'prompt_text' || field === 'pre_play_text') {
            newStep.text = value;
          } else if (field === 'retry_texts') {
            newStep.retryTexts = value;
          }
          return newStep;
        })
      }));
      return updated;
    });

    setHasChanges(true);
  };

  // 儲存
  const handleSave = async () => {
    setSaving(true);
    const edits = {};

    // 收集變數變更
    if (Object.keys(editedVariables).length > 0) {
      edits.variables = editedVariables;
    }

    // 收集 FAQ 中斷變更
    if (editedFaqInterrupt) {
      edits.faqInterrupt = editedFaqInterrupt;
    }

    // 收集文字變更
    if (textEdits.length > 0) {
      edits.textEdits = textEdits;
    }

    const result = await voiceFlowService.updateScript(flowId, edits);
    if (result.success) {
      setNotification({
        open: true,
        message: result.data?.message || '腳本已儲存',
        type: 'success'
      });
      // Fold the saved edits into `script` BEFORE clearing the edit buffers,
      // otherwise mergedVariables / faqInterrupt fall back to the stale
      // pre-save script state and the UI looks like the change reverted
      // (text edits already update script optimistically in handleTextChange,
      // so they're not affected — only variables + FAQ need this).
      setScript(prev => {
        if (!prev) return prev;
        const next = { ...prev };
        if (Object.keys(editedVariables).length > 0) {
          next.variables = { ...(prev.variables || {}), ...editedVariables };
        }
        if (editedFaqInterrupt) {
          next.faqInterrupt = { ...(prev.faqInterrupt || {}), ...editedFaqInterrupt };
        }
        return next;
      });
      setEditedVariables({});
      setEditedFaqInterrupt(null);
      setTextEdits([]);
      setHasChanges(false);
    } else {
      setNotification({ open: true, message: result.message, type: 'error' });
    }
    setSaving(false);
  };

  // ── TTS 語音產生（編輯文字後自動觸發）──
  const handleRegenerateNode = useCallback(async (nodeId, field, text) => {
    const key = `${nodeId}:${field}`;
    setRegeneratingNodes(prev => new Set(prev).add(key));

    const result = await voiceFlowService.regenerateAudio(flowId, [{ nodeId, field, text }]);

    setRegeneratingNodes(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    if (result.success) {
      const successItem = result.data.results?.find(r => r.status === 'success');
      if (successItem) {
        // 快取 CDN URL（加 cache-bust query）讓 handlePlayAudio 立即播放新音檔
        setRegeneratedUrls(prev => ({
          ...prev,
          [key]: `${successItem.cdnUrl}?v=${Date.now()}`
        }));
        // 更新 script state — 把節點的 audioAsset 換成新 key，避免下次 reload 又拿到舊版
        setScript(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            conversationPaths: prev.conversationPaths.map(path => ({
              ...path,
              steps: path.steps.map(step => {
                if (step.nodeId !== nodeId) return step;
                return { ...step, audioAsset: successItem.assetKey, audioSourceText: text };
              })
            }))
          };
        });
      }
      // 若 ASR 偵測到合成內容與文字差異過大，提示使用者人工確認
      const q = successItem?.quality;
      if (q?.warnings?.includes('asr_mismatch')) {
        const cov = q.asrCoverage ? `${Math.round(q.asrCoverage * 100)}%` : '?';
        setNotification({
          open: true,
          message: `語音已產生但 ASR 比對偏低（${cov}），建議聽看看。\nASR：${q.asrText || ''}`,
          type: 'warning'
        });
      } else {
        setNotification({ open: true, message: '語音已產生（ASR 比對通過）', type: 'success' });
      }
    } else {
      setNotification({ open: true, message: result.message || 'TTS 產生失敗', type: 'error' });
    }
  }, [flowId]);

  // 播放語音預覽
  // 優先使用 CloudFront 上的實際預錄音檔（.gsm 已轉為 .wav 上傳至 S3）
  // 若無 audioAsset 則 fallback 到瀏覽器 Web Speech API
  const handlePlayAudio = useCallback((step, onPlayStateChange) => {
    // 停止上一個播放
    if (currentAudioRef.current) { currentAudioRef.current.pause(); }
    window.speechSynthesis.cancel();

    // 0. 優先使用 TTS 重新產生的 CDN URL（以 cache-bust query 取得最新版本）
    const field = step.type === 'collect_slot' ? 'prompt_text'
      : step.type === 'api_call' ? 'pre_play_text' : 'text';
    const regenKey = `${step.nodeId}:${field}`;
    const regenUrl = regeneratedUrls[regenKey];
    if (regenUrl) {
      const audio = new Audio(regenUrl);
      currentAudioRef.current = audio;
      audio.onended = () => { currentAudioRef.current = null; onPlayStateChange(false); };
      audio.onerror = () => { currentAudioRef.current = null; playWithWebSpeech(step, onPlayStateChange); };
      onPlayStateChange(true);
      audio.play().catch(() => { currentAudioRef.current = null; playWithWebSpeech(step, onPlayStateChange); });
      return;
    }

    // 1. 有 audioAsset → 播放單一音檔
    if (step.audioAsset) {
      const audioUrl = resolveAudioUrl(step.audioAsset);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      audio.onended = () => { currentAudioRef.current = null; onPlayStateChange(false); };
      audio.onerror = () => { currentAudioRef.current = null; playWithWebSpeech(step, onPlayStateChange); };
      onPlayStateChange(true);
      audio.play().catch(() => { currentAudioRef.current = null; playWithWebSpeech(step, onPlayStateChange); });
      return;
    }

    // 2. 有 audioSegments → 播放靜態 asset 片段（跳過動態的 date/time/surname 等）
    if (step.audioSegments && step.audioSegments.length > 0) {
      const assetUrls = step.audioSegments
        .map(seg => resolveSegmentUrl(seg))
        .filter(Boolean);
      if (assetUrls.length > 0) {
        playAudioSequence(assetUrls, currentAudioRef, onPlayStateChange);
        return;
      }
    }

    // 3. 都沒有 → Web Speech fallback
    playWithWebSpeech(step, onPlayStateChange);
  }, [script?.variables, editedVariables, regeneratedUrls]);

  // Web Speech API fallback
  const playWithWebSpeech = useCallback((step, onPlayStateChange) => {
    let textToSpeak = step.text || '';
    const vars = { ...script?.variables, ...editedVariables };
    textToSpeak = textToSpeak.replace(/{{variables\.(\w+)}}/g, (_, key) => vars[key] || key);
    textToSpeak = textToSpeak.replace(/{{slots\.(\w+)}}/g, (_, key) => {
      const labels = { name: '王先生', date: '明天', time: '晚上七點', phone: '零九一二三四五六七八', adults: '四', children: '兩' };
      return labels[key] || key;
    });
    textToSpeak = textToSpeak.replace(/{{computed\.\w+}}/g, '');
    if (!textToSpeak.trim()) { onPlayStateChange(false); return; }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = 'zh-TW';
    utterance.rate = 0.9;
    utterance.onend = () => onPlayStateChange(false);
    utterance.onerror = () => onPlayStateChange(false);
    onPlayStateChange(true);
    window.speechSynthesis.speak(utterance);
  }, [script?.variables, editedVariables]);

  // 停止任何正在播放的音檔
  const handleStopAudio = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    window.speechSynthesis.cancel();
  }, []);

  // 合併變數（原始 + 編輯）
  const mergedVariables = script?.variables
    ? { ...script.variables, ...editedVariables }
    : {};

  // 合併 FAQ 中斷設定
  const mergedFaqInterrupt = editedFaqInterrupt || script?.faqInterrupt || null;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width="33%" height={400} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="67%" height={400} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      {/* 頂部工具列 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {flowMeta?.name || flowId}
          </Typography>
          {flowMeta?.triggerType && (
            <Chip
              label={flowMeta.triggerType === 'inbound' ? '進線' : flowMeta.triggerType === 'outbound' ? '外撥' : flowMeta.triggerType}
              size="small"
              color={flowMeta.triggerType === 'inbound' ? 'primary' : 'secondary'}
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
          {hasChanges && (
            <Chip
              label="未儲存"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100' }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={hasChanges ? '儲存變更' : '無變更'}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                儲存
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* 子分頁 — 腳本 / 限制 */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          minHeight: 36,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
          '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontSize: '0.875rem' }
        }}
      >
        <Tab label="對話腳本" value="script" />
        <Tab label="日期/時間限制" value="constraints" />
      </Tabs>

      {/* 內容區 */}
      {activeTab === 'script' ? (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* 左面板 — 設定 (1/3) */}
          <Box
            sx={{
              width: '33%',
              minWidth: 280,
              maxWidth: 360,
              borderRight: '1px solid #e0e0e0',
              overflow: 'hidden',
            }}
          >
            <FlowSettingsPanel
              flowName={flowMeta?.name || flowId}
              triggerConditions={flowMeta?.triggerConditions}
              variables={mergedVariables}
              slots={script?.slots || []}
              faqInterrupt={mergedFaqInterrupt}
              onVariableChange={handleVariableChange}
              onFaqInterruptChange={handleFaqInterruptChange}
            />
          </Box>

          {/* 右面板 — 對話腳本 (2/3) */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <ConversationScript
                conversationPaths={script?.conversationPaths || []}
                activePathId={activePathId}
                onPathChange={setActivePathId}
                onTextChange={handleTextChange}
                onPlayAudio={handlePlayAudio}
                onStopAudio={handleStopAudio}
                regeneratingNodes={regeneratingNodes}
                onRegenerateNode={handleRegenerateNode}
                slots={script?.slots || []}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ConstraintTab flowId={flowId} flowName={flowMeta?.name || flowId} />
        </Box>
      )}

      {/* 通知 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScriptEditor;
